import React, { useState, useEffect } from 'react';
import { 
  Package, 
  DollarSign, 
  RefreshCw, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff,
  Calendar,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import apiService from '../services/api';
import { SubscriptionOption, StripeProduct, StripePrice, SyncResult } from '../types';

const SubscriptionPackages: React.FC = () => {
  const [subscriptionOptions, setSubscriptionOptions] = useState<SubscriptionOption[]>([]);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [prices, setPrices] = useState<StripePrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('usd');
  const [viewMode, setViewMode] = useState<'options' | 'products' | 'prices'>('options');
  const [showSyncDetails, setShowSyncDetails] = useState(false);

  const currencies = [
    { code: 'usd', name: 'US Dollar', symbol: '$' },
    { code: 'eur', name: 'Euro', symbol: '€' },
    { code: 'gbp', name: 'British Pound', symbol: '£' },
    { code: 'cad', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'aud', name: 'Australian Dollar', symbol: 'A$' },
  ];

  useEffect(() => {
    loadData();
  }, [selectedCurrency, viewMode]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      switch (viewMode) {
        case 'options':
          const options = await apiService.getSubscriptionOptionsByCurrency(selectedCurrency);
          setSubscriptionOptions(options);
          break;
        case 'products':
          const productsResponse = await apiService.getStripeProducts();
          setProducts(productsResponse.results);
          break;
        case 'prices':
          const pricesResponse = await apiService.getStripePrices(1, undefined, selectedCurrency);
          setPrices(pricesResponse.results);
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (syncType: 'products' | 'subscriptions' | 'all') => {
    try {
      setSyncing(true);
      setError(null);
      let result: SyncResult;

      switch (syncType) {
        case 'products':
          result = await apiService.syncStripeProductsAndPrices();
          break;
        case 'subscriptions':
          result = await apiService.syncStripeSubscriptions();
          break;
        case 'all':
          result = await apiService.syncAllStripeData();
          break;
      }

      setSyncResult(result);
      setShowSyncDetails(true);
      
      // Reload data after sync
      await loadData();
      
      // Hide sync details after 5 seconds
      setTimeout(() => setShowSyncDetails(false), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'inactive':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    const symbol = currencies.find(c => c.code === currency)?.symbol || '$';
    return `${symbol}${(amount / 100).toFixed(2)}`;
  };

  const getIntervalText = (interval?: string, count?: number) => {
    if (!interval) return 'One-time';
    const intervalText = interval.charAt(0).toUpperCase() + interval.slice(1);
    return count && count > 1 ? `Every ${count} ${intervalText}s` : `Per ${intervalText}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Package className="w-8 h-8 text-primary" />
              Subscription Packages
            </h1>
            <p className="mt-2 text-gray-600">
              Manage your Stripe subscription offerings and pricing
            </p>
          </div>
          
          {/* Sync Button */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleSync('all')}
              disabled={syncing}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync from Stripe'}
            </button>
          </div>
        </div>

        {/* Sync Result */}
        {showSyncDetails && syncResult && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">Sync completed successfully!</span>
              </div>
              <button
                onClick={() => setShowSyncDetails(false)}
                className="text-blue-600 hover:text-blue-800"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-blue-800">
              <div>Products created: {syncResult.products_created}</div>
              <div>Products updated: {syncResult.products_updated}</div>
              <div>Prices created: {syncResult.prices_created}</div>
              <div>Prices updated: {syncResult.prices_updated}</div>
              {syncResult.subscriptions_created !== undefined && (
                <div>Subscriptions created: {syncResult.subscriptions_created}</div>
              )}
              {syncResult.subscriptions_updated !== undefined && (
                <div>Subscriptions updated: {syncResult.subscriptions_updated}</div>
              )}
            </div>
            {syncResult.errors.length > 0 && (
              <div className="mt-2">
                <p className="font-medium text-red-800">Errors:</p>
                <ul className="list-disc list-inside text-red-700">
                  {syncResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        {/* View Mode Tabs */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('options')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'options'
                ? 'bg-white text-primary shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Subscription Options
          </button>
          <button
            onClick={() => setViewMode('products')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'products'
                ? 'bg-white text-primary shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Products
          </button>
          <button
            onClick={() => setViewMode('prices')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'prices'
                ? 'bg-white text-primary shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Prices
          </button>
        </div>

        {/* Currency Selector */}
        <select
          value={selectedCurrency}
          onChange={(e) => setSelectedCurrency(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
        >
          {currencies.map((currency) => (
            <option key={currency.code} value={currency.code}>
              {currency.symbol} {currency.name}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {viewMode === 'options' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subscriptionOptions.map((option) => (
            <div key={option.product.id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{option.product.name}</h3>
                  {getStatusIcon(option.product.is_active ? 'active' : 'inactive')}
                </div>
                
                <p className="text-gray-600 mb-4 line-clamp-2">{option.product.description}</p>
                
                <div className="space-y-3">
                  {option.prices.map((price) => (
                    <div key={price.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div>
                        <div className="font-medium text-gray-900">{price.formatted_price}</div>
                        <div className="text-sm text-gray-500">
                          {getIntervalText(price.recurring_interval, price.recurring_interval_count)}
                        </div>
                        {price.nickname && (
                          <div className="text-xs text-gray-400">{price.nickname}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewMode === 'products' && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Products</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prices
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Synced
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                        <div className="text-sm text-gray-500">{product.stripe_product_id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusIcon(product.is_active ? 'active' : 'inactive')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.price_count} prices
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(product.last_synced).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button className="text-primary hover:text-primary-dark">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="text-primary hover:text-primary-dark">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewMode === 'prices' && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Prices</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Interval
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {prices.map((price) => (
                  <tr key={price.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{price.formatted_price}</div>
                        <div className="text-sm text-gray-500">{price.nickname}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {price.product?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getIntervalText(price.recurring_interval, price.recurring_interval_count)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusIcon(price.is_active ? 'active' : 'inactive')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button className="text-primary hover:text-primary-dark">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="text-primary hover:text-primary-dark">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {((viewMode === 'options' && subscriptionOptions.length === 0) ||
        (viewMode === 'products' && products.length === 0) ||
        (viewMode === 'prices' && prices.length === 0)) && (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No {viewMode} found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {viewMode === 'options' && 'Get started by syncing your Stripe products and prices.'}
            {viewMode === 'products' && 'No products found. Sync from Stripe to get started.'}
            {viewMode === 'prices' && 'No prices found for the selected currency.'}
          </p>
          <div className="mt-6">
            <button
              onClick={() => handleSync('products')}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync from Stripe
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPackages; 